import { CONSTELLATION_LINES_URL, STAR_MAPPING_URL } from '../constants.js';

/**
 * Normalize free-form lookup keys (city/star names) for consistent indexes.
 * @param {string} value
 * @returns {string}
 */
const normalizeKey = (value) =>
	typeof value === 'string' ? value.trim().toLowerCase() : '';

const MAX_STAR_LINE_LONGITUDE_SPAN = 45;

/**
 * Add constellation/starmap helpers to MapApp.
 * @param {typeof import('../app.js').default} App
 */
export function applyConstellationMixins(App) {
	/**
	 * Build a lookup of city slugs/names to lat/lon coordinates.
	 */
	App.prototype.initializeCityCoordinateIndex = function initializeCityCoordinateIndex() {
		this.cityCoordinateIndex = new Map();
		if (!Array.isArray(globalThis.cities)) {
			return;
		}
		const register = (key, lat, lon) => {
			if (!key || typeof lat !== 'number' || typeof lon !== 'number') {
				return;
			}
			this.cityCoordinateIndex.set(normalizeKey(key), { lat, lon });
		};
		for (let i = 0; i < globalThis.cities.length; i += 1) {
			const city = globalThis.cities[i];
			if (!city) {
				continue;
			}
			register(city.name, city.lat, city.lon);
			register(city.slug, city.lat, city.lon);
		}
	};

	/**
	 * Update the coordinate cache for one or more keys (name/slug).
	 * @param {string|string[]} keys
	 * @param {number} lat
	 * @param {number} lon
	 */
	App.prototype.updateCityCoordinateIndex = function updateCityCoordinateIndex(keys, lat, lon) {
		if (typeof lat !== 'number' || typeof lon !== 'number') {
			return;
		}
		if (!this.cityCoordinateIndex) {
			this.cityCoordinateIndex = new Map();
		}
		const register = (key) => {
			if (!key) {
				return;
			}
			const normalized = normalizeKey(key);
			if (normalized) {
				this.cityCoordinateIndex.set(normalized, { lat, lon });
			}
		};
		if (Array.isArray(keys)) {
			for (let i = 0; i < keys.length; i += 1) {
				register(keys[i]);
			}
		} else {
			register(keys);
		}
	};

	/**
	 * Look up coordinates for a city by name or slug.
	 * @param {string} key
	 * @returns {{lat:number,lon:number}|null}
	 */
	App.prototype.getCityCoordinates = function getCityCoordinates(key) {
		if (!key || !this.cityCoordinateIndex) {
			return null;
		}
		return this.cityCoordinateIndex.get(normalizeKey(key)) || null;
	};

	/**
	 * Create the Leaflet layer used for star connecting lines.
	 */
	App.prototype.setupStarLineLayer = function setupStarLineLayer() {
		if (!this.map || this.starLineLayer) {
			return;
		}
		this.map.createPane(this.starLinePane);
		const pane = this.map.getPane(this.starLinePane);
		if (pane) {
			pane.style.zIndex = '340';
			pane.style.pointerEvents = 'none';
		}
		this.starLineLayer = globalThis.L.layerGroup([], {
			pane: this.starLinePane
		});
		this.starLineSegments = [];
		this.starLinesByCity = new Map();
	};

	/**
	 * Create the pane/layer used for synthetic (shadow) star markers.
	 */
	App.prototype.setupSyntheticStarLayer = function setupSyntheticStarLayer() {
		if (!this.map || this.syntheticStarLayer) {
			return;
		}
		const paneName = this.syntheticStarPane || this.starLinePane;
		if (typeof this.map.getPane === 'function') {
			let pane = this.map.getPane(paneName);
			if (!pane && typeof this.map.createPane === 'function') {
				this.map.createPane(paneName);
				pane = this.map.getPane(paneName);
			}
			if (pane) {
				pane.style.zIndex = '345';
				pane.style.pointerEvents = 'none';
			}
		}
		this.syntheticStarLayer = globalThis.L.layerGroup([], {
			pane: paneName
		});
		this.syntheticCityEntries = [];
		this.updateSyntheticPaneInteractivity();
	};

	/**
	 * Load star mapping and constellation line definitions once.
	 * @returns {Promise<void>}
	 */
	App.prototype.loadStarData = function loadStarData() {
		if (this.starDataLoaded || this.pendingStarDataLoad || typeof fetch !== 'function') {
			return this.starDataPromise || Promise.resolve(null);
		}
		this.pendingStarDataLoad = true;
		this.starDataPromise = Promise.all([
			fetch(STAR_MAPPING_URL).then((response) => {
				if (!response.ok) {
					throw new Error('Failed to load star mapping');
				}
				return response.json();
			}),
			fetch(CONSTELLATION_LINES_URL).then((response) => {
				if (!response.ok) {
					throw new Error('Failed to load constellation lines');
				}
				return response.json();
			})
		])
		.then(([mapping, lines]) => {
			this.starMappingData = mapping || null;
			this.constellationLineDefs = lines || null;
			this.starDataLoaded = !!this.starMappingData && !!this.constellationLineDefs;
			if (this.starDataLoaded) {
				this.ensureStarLines();
				this.showSyntheticStars();
				if (
					this.isEditMode &&
					typeof this.isEditMode === 'function' &&
					this.isEditMode() &&
					this.lastDebugSelection &&
					this.lastDebugSelection.entity
				) {
					this.showDebugInfo(
						this.lastDebugSelection.kind,
						this.lastDebugSelection.entity
					);
				}
			}
		})
			.catch((error) => {
				console.warn(error);
			})
			.finally(() => {
				this.pendingStarDataLoad = false;
			});
		return this.starDataPromise;
	};

	/**
	 * Ensure star line segments exist or refresh their geometry.
	 */
	App.prototype.ensureStarLines = function ensureStarLines() {
		if (!this.map || !this.starLineLayer || !this.starMappingData || !this.constellationLineDefs) {
			return;
		}
		if (this.starLineSegments.length) {
			this.refreshStarLineGeometry();
			return;
		}
		this.buildStarLineSegments();
	};

	/**
	 * Create a Leaflet polyline for a constellation segment.
	 * @param {Array<Array<number>>} latLngs
	 * @returns {L.Polyline}
	 */
	App.prototype.createStarPolyline = function createStarPolyline(latLngs) {
		const line = globalThis.L.polyline(latLngs, {
			color: 'rgba(247, 243, 255, 0.4)',
			weight: 1.2,
			opacity: 0.55,
			pane: this.starLinePane,
			interactive: false
		});
		line.addTo(this.starLineLayer);
		return line;
	};

	/**
	 * Split a segment into multiple lat/lon chunks when crossing ±180°.
	 */
	App.prototype.computeSegmentChunks = function computeSegmentChunks(pointA, pointB) {
		if (!pointA || !pointB) {
			return [];
		}
		const latA = pointA.lat;
		const lonA = pointA.lon;
		const latB = pointB.lat;
		const lonB = pointB.lon;
		if (
			!Number.isFinite(latA) ||
			!Number.isFinite(lonA) ||
			!Number.isFinite(latB) ||
			!Number.isFinite(lonB)
		) {
			return [];
		}
		const diff = lonB - lonA;
		if (diff > 180 || diff < -180) {
			const boundary = diff > 0 ? 180 : -180;
			const adjustedLonB = diff > 0 ? lonB - 360 : lonB + 360;
			const denominator = adjustedLonB - lonA;
			if (denominator === 0) {
				return [
					[
						[latA, lonA],
						[latB, lonB]
					]
				];
			}
			const ratio = (boundary - lonA) / denominator;
			const clampedRatio = Math.min(Math.max(ratio, 0), 1);
			const latCross = latA + (latB - latA) * clampedRatio;
			const wrapLon = diff > 0 ? -180 : 180;
			return [
				[
					[latA, lonA],
					[latCross, boundary]
				],
				[
					[latCross, wrapLon],
					[latB, lonB]
				]
			];
		}
		return [
			[
				[latA, lonA],
				[latB, lonB]
			]
		];
	};

	/**
	 * Compute the absolute longitude separation between two points.
	 */
	App.prototype.computeLongitudeSpan = function computeLongitudeSpan(pointA, pointB) {
		if (!pointA || !pointB) {
			return Number.POSITIVE_INFINITY;
		}
		const lonA = pointA.lon;
		const lonB = pointB.lon;
		if (!Number.isFinite(lonA) || !Number.isFinite(lonB)) {
			return Number.POSITIVE_INFINITY;
		}
		return Math.abs(lonA - lonB);
	};

	/**
	 * Recompute the polyline geometry for a star segment.
	 */
	App.prototype.updateSegmentPolylines = function updateSegmentPolylines(segment, coordsA, coordsB) {
		if (!segment) {
			return;
		}
		const separation = this.computeLongitudeSpan(coordsA, coordsB);
		if (!Number.isFinite(separation) || separation > MAX_STAR_LINE_LONGITUDE_SPAN) {
			if (Array.isArray(segment.polylines) && this.starLineLayer) {
				for (let i = 0; i < segment.polylines.length; i += 1) {
					const polyline = segment.polylines[i];
					if (polyline) {
						this.starLineLayer.removeLayer(polyline);
					}
				}
			}
			segment.polylines = [];
			const fallbackA = coordsA || {};
			const fallbackB = coordsB || {};
			segment.fallback = [
				{ lat: fallbackA.lat, lon: fallbackA.lon },
				{ lat: fallbackB.lat, lon: fallbackB.lon }
			];
			return;
		}
		const chunks = this.computeSegmentChunks(coordsA, coordsB);
		if (!chunks.length) {
			return;
		}
		if (!Array.isArray(segment.polylines)) {
			segment.polylines = [];
		}
		const polylines = [];
		for (let i = 0; i < chunks.length; i += 1) {
			const latLngs = chunks[i];
			let line = segment.polylines[i];
			if (line) {
				line.setLatLngs(latLngs);
			} else {
				line = this.createStarPolyline(latLngs);
			}
			polylines.push(line);
		}
		for (let i = chunks.length; i < segment.polylines.length; i += 1) {
			const stale = segment.polylines[i];
			if (stale && this.starLineLayer) {
				this.starLineLayer.removeLayer(stale);
			}
		}
		segment.polylines = polylines;
		segment.fallback = [
			{ lat: coordsA.lat, lon: coordsA.lon },
			{ lat: coordsB.lat, lon: coordsB.lon }
		];
	};

	/**
	 * Register a segment and keep lookup sets for per-city updates.
	 */
	App.prototype.registerStarLineSegment = function registerStarLineSegment(segment) {
		if (!segment) {
			return;
		}
		this.starLineSegments.push(segment);
		this.updateSegmentPolylines(segment, segment.fallback[0], segment.fallback[1]);
		if (!Array.isArray(segment.cities)) {
			return;
		}
		for (let c = 0; c < segment.cities.length; c += 1) {
			const cityKey = segment.cities[c];
			if (!cityKey) {
				continue;
			}
			if (!this.starLinesByCity.has(cityKey)) {
				this.starLinesByCity.set(cityKey, []);
			}
			this.starLinesByCity.get(cityKey).push(segment);
		}
	};

	/**
	 * Build star-line segments for every nation from line definitions.
	 */
	App.prototype.buildStarLineSegments = function buildStarLineSegments() {
		if (!this.starLineLayer || !this.starMappingData || !this.constellationLineDefs) {
			return;
		}
		this.starLineLayer.clearLayers();
		this.starLineSegments = [];
		this.starLinesByCity = new Map();

		const mappingEntries = Object.entries(this.starMappingData);
		for (let i = 0; i < mappingEntries.length; i += 1) {
			const [nationSlug, info] = mappingEntries[i];
			if (!info || !Array.isArray(info.cities)) {
				continue;
			}
			const linePairs = this.constellationLineDefs[nationSlug];
			if (!Array.isArray(linePairs) || !linePairs.length) {
				continue;
			}

			const starCityLookup = new Map();
			for (let j = 0; j < info.cities.length; j += 1) {
				const cityEntry = info.cities[j];
				if (!cityEntry) {
					continue;
				}
				const coords = cityEntry.coordinates || {};
				if (typeof coords.lat !== 'number' || typeof coords.lon !== 'number') {
					continue;
				}
				const payload = {
					lat: coords.lat,
					lon: coords.lon,
					key: normalizeKey(cityEntry.slug || cityEntry.name || cityEntry.star),
					cityName: cityEntry.name || cityEntry.slug || 'unknown',
					slug: cityEntry.slug || null,
					synthetic: !!cityEntry.synthetic
				};
				const registrationKeys = [
					cityEntry.slug,
					cityEntry.name,
					cityEntry.city,
					cityEntry.star
				];
				this.updateCityCoordinateIndex(registrationKeys, payload.lat, payload.lon);
				for (let k = 0; k < registrationKeys.length; k += 1) {
					const key = normalizeKey(registrationKeys[k]);
					if (key) {
						starCityLookup.set(key, payload);
					}
				}
			}

			for (let k = 0; k < linePairs.length; k += 1) {
				const pair = linePairs[k];
				if (!Array.isArray(pair) || pair.length < 2) {
					continue;
				}
				const keyA = normalizeKey(pair[0]);
				const keyB = normalizeKey(pair[1]);
				if (!keyA || !keyB) {
					continue;
				}
				const pointA = starCityLookup.get(keyA);
				const pointB = starCityLookup.get(keyB);
				if (!pointA || !pointB) {
					continue;
				}
				const separation = this.computeLongitudeSpan(pointA, pointB);
				if (!Number.isFinite(separation) || separation > MAX_STAR_LINE_LONGITUDE_SPAN) {
					continue;
				}
				const segment = {
					polylines: [],
					cities: [pointA.key || keyA, pointB.key || keyB],
					fallback: [
						{ lat: pointA.lat, lon: pointA.lon },
						{ lat: pointB.lat, lon: pointB.lon }
					]
				};
				this.registerStarLineSegment(segment);
			}
		}
	};

	/**
	 * Update all existing star-line segments with latest coordinates.
	 */
	App.prototype.refreshStarLineGeometry = function refreshStarLineGeometry() {
		if (!this.starLineSegments.length) {
			return;
		}
		for (let i = 0; i < this.starLineSegments.length; i += 1) {
			const segment = this.starLineSegments[i];
			if (!segment || !Array.isArray(segment.cities)) {
				continue;
			}
			const coordsA =
				this.getCityCoordinates(segment.cities[0]) || segment.fallback[0] || null;
			const coordsB =
				this.getCityCoordinates(segment.cities[1]) || segment.fallback[1] || null;
			if (!coordsA || !coordsB) {
				continue;
			}
			this.updateSegmentPolylines(segment, coordsA, coordsB);
		}
	};

	/**
	 * Recompute any segments that reference the given city identifiers.
	 * @param {string|string[]} identifiers
	 */
	App.prototype.updateStarLinesForCity = function updateStarLinesForCity(identifiers) {
		if (!this.starLinesByCity || !this.starLinesByCity.size) {
			return;
		}
		const keys = Array.isArray(identifiers) ? identifiers : [identifiers];
		const normalized = [];
		for (let i = 0; i < keys.length; i += 1) {
			const key = normalizeKey(keys[i]);
			if (key) {
				normalized.push(key);
			}
		}
		const segments = new Set();
		for (let i = 0; i < normalized.length; i += 1) {
			const list = this.starLinesByCity.get(normalized[i]);
			if (Array.isArray(list)) {
				for (let j = 0; j < list.length; j += 1) {
					segments.add(list[j]);
				}
			}
		}
		segments.forEach((segment) => {
			if (!segment || !Array.isArray(segment.cities)) {
				return;
			}
			const coordsA =
				this.getCityCoordinates(segment.cities[0]) || segment.fallback[0] || null;
			const coordsB =
				this.getCityCoordinates(segment.cities[1]) || segment.fallback[1] || null;
			if (coordsA && coordsB) {
				this.updateSegmentPolylines(segment, coordsA, coordsB);
			}
		});
	};

	/**
	 * Ensure constellation lines are visible on the map.
	 */
	App.prototype.showConstellationLines = function showConstellationLines() {
		if (!this.map) {
			return;
		}
		if (!this.starLineLayer) {
			this.setupStarLineLayer();
		}
		if (!this.starDataLoaded) {
			this.loadStarData().then(() => {
				this.showConstellationLines();
			});
			return;
		}
		this.ensureStarLines();
		if (this.starLineLayer && !this.map.hasLayer(this.starLineLayer)) {
			this.starLineLayer.addTo(this.map);
		}
	};

	/**
	 * Remove constellation lines from the map.
	 */
	App.prototype.hideConstellationLines = function hideConstellationLines() {
		if (this.map && this.starLineLayer && this.map.hasLayer(this.starLineLayer)) {
			this.map.removeLayer(this.starLineLayer);
		}
	};

	/**
	 * Lazy-create the synthetic star marker icon.
	 */
	App.prototype.getSyntheticCityIcon = function getSyntheticCityIcon() {
		if (this.syntheticCityIcon) {
			return this.syntheticCityIcon;
		}
		this.syntheticCityIcon = globalThis.L.divIcon({
			className: 'synthetic-dot-icon',
			html: '<span class="synthetic-dot-icon__inner"></span>',
			iconSize: [8, 8],
			iconAnchor: [4, 4]
		});
		return this.syntheticCityIcon;
	};

	/**
	 * Render synthetic markers (shadow stars) if data is available.
	 */
	App.prototype.renderSyntheticStars = function renderSyntheticStars() {
		if (!this.syntheticStarLayer || !this.starMappingData) {
			return;
		}
		this.syntheticStarLayer.clearLayers();
		this.syntheticCityEntries = [];
		const icon = this.getSyntheticCityIcon();
		const paneName = this.syntheticStarPane || this.starLinePane;
		const includeAllStars = !!this.starDotModeCompact;
		const allowDrag = this.isEditMode();
		for (const [nationSlug, nation] of Object.entries(this.starMappingData)) {
			for (const city of (nation && nation.cities) || []) {
				if (!city) {
					continue;
				}
				if (!city.synthetic && !includeAllStars) {
					continue;
				}
				const coords = city.coordinates || {};
				if (typeof coords.lat !== 'number' || typeof coords.lon !== 'number') {
					continue;
				}
				if (!city.nationSlug) {
					city.nationSlug = nationSlug;
				}
				const marker = globalThis.L.marker([coords.lat, coords.lon], {
					icon,
					keyboard: false,
					autoPan: false,
					bubblingMouseEvents: false,
					zIndexOffset: includeAllStars ? 0 : -50,
					opacity: 1,
					draggable: allowDrag && !!city.synthetic,
					pane: paneName
				});
				marker.addTo(this.syntheticStarLayer);
				const entry = {
					marker,
					city,
					nationSlug,
					slug: city.slug || city.star || `${nationSlug}-synthetic`
				};
				marker.on('click', (event) => {
					if (!this.isEditMode || !this.isEditMode()) {
						return;
					}
					this.onSyntheticCityMarkerClick(entry, event);
				});
				if (city.synthetic) {
					marker.on('dragend', (event) => {
						this.handleSyntheticCityDragEnd(entry, event);
					});
				}
				this.syntheticCityEntries.push(entry);
			}
		}
		if (this.map && !this.map.hasLayer(this.syntheticStarLayer)) {
			this.syntheticStarLayer.addTo(this.map);
		}
		this.applyModeToSyntheticMarkers();
	};

	/**
	 * Ensure the synthetic star markers are present and synced with the mode.
	 */
	App.prototype.showSyntheticStars = function showSyntheticStars() {
		if (!this.map) {
			return;
		}
		if (!this.syntheticStarLayer) {
			this.setupSyntheticStarLayer();
		}
		if (!this.starDataLoaded) {
			this.loadStarData().then(() => {
				this.showSyntheticStars();
			});
			return;
		}
		this.renderSyntheticStars();
		if (typeof this.updateCompactStarDots === 'function') {
			this.updateCompactStarDots();
		}
	};

	/**
	 * Remove the synthetic star layer from the map if present.
	 */
	App.prototype.hideSyntheticStars = function hideSyntheticStars() {
		if (this.map && this.syntheticStarLayer && this.map.hasLayer(this.syntheticStarLayer)) {
			this.map.removeLayer(this.syntheticStarLayer);
		}
	};

	/**
	 * Toggle edit affordances for synthetic markers when mode changes.
	 */
	App.prototype.applyModeToSyntheticMarkers = function applyModeToSyntheticMarkers() {
		if (!Array.isArray(this.syntheticCityEntries)) {
			return;
		}
		const draggable = this.isEditMode();
		for (let i = 0; i < this.syntheticCityEntries.length; i += 1) {
			this.setSyntheticMarkerDraggable(this.syntheticCityEntries[i], draggable);
		}
		this.updateSyntheticPaneInteractivity();
	};

	/**
	 * Enable/disable dragging and styling for a synthetic entry.
	 * @param {object} entry
	 * @param {boolean} draggable
	 */
	App.prototype.setSyntheticMarkerDraggable = function setSyntheticMarkerDraggable(entry, draggable) {
		if (!entry || !entry.marker || !entry.marker.dragging || !entry.city || !entry.city.synthetic) {
			return;
		}
		if (draggable) {
			entry.marker.dragging.enable();
		} else {
			entry.marker.dragging.disable();
		}
		const el = entry.marker.getElement();
		if (el) {
			if (draggable) {
				el.classList.add('is-editable');
			} else {
				el.classList.remove('is-editable');
			}
		}
	};

	/**
	 * Allow pointer events on the synthetic pane only while editing.
	 */
	App.prototype.updateSyntheticPaneInteractivity = function updateSyntheticPaneInteractivity() {
		if (!this.map || typeof this.map.getPane !== 'function') {
			return;
		}
		const paneName = this.syntheticStarPane || this.starLinePane;
		const pane = this.map.getPane(paneName);
		if (!pane) {
			return;
		}
		const interactive = typeof this.isEditMode === 'function' && this.isEditMode();
		pane.style.pointerEvents = interactive ? 'auto' : 'none';
	};

	/**
	 * Determine whether zoom level warrants compact star-dot rendering.
	 * @returns {boolean}
	 */
	App.prototype.shouldEnableCompactStarDots = function shouldEnableCompactStarDots() {
		if (!this.map || typeof this.isStarMode !== 'function' || !this.isStarMode()) {
			return false;
		}
		const zoom = typeof this.map.getZoom === 'function' ? this.map.getZoom() : this.defaultZoomLevel || 0;
		const baseline =
			typeof this.defaultZoomLevel === 'number' ? this.defaultZoomLevel : this.cityZoomThreshold || 3;
		return zoom < baseline;
	};

	/**
	 * Reflect the compact star-dot state on the map container element.
	 * @param {boolean} enable
	 */
	App.prototype.setStarDotModeAttribute = function setStarDotModeAttribute(enable) {
		if (!this.map || typeof this.map.getContainer !== 'function') {
			return;
		}
		const container = this.map.getContainer();
		if (!container) {
			return;
		}
		if (enable) {
			container.setAttribute('data-star-dot-mode', 'compact');
		} else {
			container.removeAttribute('data-star-dot-mode');
		}
	};

	/**
	 * Re-render synthetic markers if the compact/star mode needs to change.
	 */
	App.prototype.updateCompactStarDots = function updateCompactStarDots() {
		if (!this.syntheticStarLayer || !this.starMappingData) {
			this.setStarDotModeAttribute(false);
			this.starDotModeCompact = false;
			return;
		}
		const shouldCompact = this.shouldEnableCompactStarDots();
		if (shouldCompact !== this.starDotModeCompact) {
			this.starDotModeCompact = shouldCompact;
			this.renderSyntheticStars();
		}
		this.setStarDotModeAttribute(shouldCompact);
	};

	/**
	 * Persist star coordinates and refresh lines after a synthetic drag.
	 * @param {object} entry
	 * @param {L.LeafletEvent} event
	 */
	App.prototype.handleSyntheticCityDragEnd = function handleSyntheticCityDragEnd(entry, event) {
		if (!entry || !entry.marker || !entry.city) {
			return;
		}
		const latLng =
			(event && event.latlng) ? event.latlng : entry.marker.getLatLng();
		if (!latLng) {
			return;
		}
		entry.city.coordinates = entry.city.coordinates || {};
		entry.city.coordinates.lat = latLng.lat;
		entry.city.coordinates.lon = latLng.lng;
		if (entry.nationSlug && !entry.city.nationSlug) {
			entry.city.nationSlug = entry.nationSlug;
		}
		this.updateStarMappingCoordinates('city', entry.city, latLng.lat, latLng.lng);
		const idKeys = [entry.city.slug, entry.city.name, entry.city.star];
		this.updateCityCoordinateIndex(idKeys, latLng.lat, latLng.lng);
		this.updateStarLinesForCity(idKeys);
		if (this.isEditMode && this.isEditMode()) {
			this.showDebugInfo('city', entry.city, { latlng: latLng });
		}
	};

	/**
	 * Surface debugging info for a synthetic entry when clicked.
	 * @param {object} entry
	 * @param {L.LeafletEvent} event
	 */
	App.prototype.onSyntheticCityMarkerClick = function onSyntheticCityMarkerClick(entry, event) {
		if (!entry || !entry.city) {
			return;
		}
		const latlng =
			event && event.latlng
				? event.latlng
				: entry.marker && typeof entry.marker.getLatLng === 'function'
					? entry.marker.getLatLng()
					: null;
		if (entry.nationSlug && !entry.city.nationSlug) {
			entry.city.nationSlug = entry.nationSlug;
		}
		this.showDebugInfo('city', entry.city, {
			latlng,
			syntheticEntry: entry
		});
	};
}
